/**
 * Practice-round gate — shown after the reader completes the minimum number
 * of practice cases. Tells them they're done with practice and gives them a
 * "Begin Study" button that transitions to the real study set.
 *
 * The gate also displays the current practice count so the rad knows how
 * many they've done (and can do more for extra warmup if they want).
 */

import { PRACTICE_CASE_MIN } from "../../atoms/studyOnboardingAtoms";

export interface StudyPracticeGateProps {
  /** Number of practice cases the reader has signed so far. */
  practiceSignedCount: number;
  /** Whether the minimum number of practice cases has been completed. */
  canBeginStudy: boolean;
  /** Called when the reader clicks "Begin Study". */
  onBeginStudy: () => void;
  /**
   * Called when the reader wants to do another practice case before beginning
   * the study. Only shown when `canBeginStudy` is true.
   */
  onMorePractice: () => void;
}

export const StudyPracticeGate: React.FC<StudyPracticeGateProps> = ({
  practiceSignedCount,
  canBeginStudy,
  onBeginStudy,
  onMorePractice,
}) => {
  if (!canBeginStudy) {
    // Still in practice — show progress but no gate overlay
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-[2px]"
      data-testid="study-practice-gate"
      role="dialog"
      aria-modal="true"
      aria-label="Practice complete"
    >
      <div className="bg-bg-2 border border-border-strong rounded shadow-2xl px-8 py-7 max-w-md w-full mx-4 text-center">
        <p className="text-xs font-semibold uppercase tracking-widest text-signal-text-tint mb-2">
          Practice complete
        </p>
        <h2 className="text-xl font-bold text-white mb-3">Ready to begin the study?</h2>
        <p className="text-sm text-fg-muted mb-2">
          You have completed <strong className="text-white">{practiceSignedCount}</strong> practice{" "}
          {practiceSignedCount === 1 ? "case" : "cases"} (minimum is {PRACTICE_CASE_MIN}).
        </p>
        <p className="text-sm text-fg-muted mb-6">
          Practice reports, timings, and ratings are excluded from the study dataset. Click{" "}
          <strong className="text-white">Begin Study</strong> when you are ready — once you proceed,
          the timer and data collection start for real.
        </p>

        <div className="flex flex-col gap-3">
          <button
            type="button"
            onClick={onBeginStudy}
            className="w-full px-5 py-3 text-sm font-medium rounded-lg bg-signal hover:bg-signal/90 text-white"
            data-testid="study-practice-gate-begin"
          >
            Begin Study
          </button>
          <button
            type="button"
            onClick={onMorePractice}
            className="w-full px-4 py-2 text-sm text-fg-muted hover:text-white"
            data-testid="study-practice-gate-more"
          >
            Do another practice case first
          </button>
        </div>
      </div>
    </div>
  );
};
